import { ContentCard } from "@msuite/ui/content-card";
import { DataGrid } from "@msuite/ui/data-grid";
import { icon } from "@msuite/ui/icons";
import { LazyFallback } from "@msuite/ui/loading-screen";
import { useTranslation } from "@msuite/ui/localization";
import { Panels } from "@msuite/ui/panels";
import { Tag } from "@msuite/ui/tag";
import { guardString } from "@msuite/utils/strings";
import { lazy, Suspense } from "react";
import { useTransferCarrier } from "@/hooks/use-transfer-carrier";
import type { TransferCarrierPanelBaseProps } from "../shared";
import { TransferCarrierLabel } from "./transfer-carrier-label";

const Notes = lazy(() => import("@/components/notes"));

/**
 * What one trolley is and where it stands.
 *
 * There is no second panel of figures here, which is the difference from a load carrier's page:
 * that one has a balance, a reservation and an orderable quantity because it is a counted type.
 * This is one object, so the only numbers it has are its label and what is currently on it, and
 * both sit in the narrow column together with the printed label — a trolley nobody can scan is a
 * trolley that has to be found by walking the yard.
 *
 * The note thread takes the wider column because it is the part that grows: the cards beside it
 * state facts that fit in a screen and stay that size, while a trolley that goes missing, comes
 * back damaged or is argued over at the gate accumulates.
 *
 * "Where it stands" is the end of its last **finished** leg rather than where it is heading, so a
 * loaded trolley still reads as the zone it left. That is the answer somebody looking for it
 * wants — the destination is on the load, not on the device.
 *
 * `parent` is the trolley itself. Reading and writing a note follow the parent's own read right,
 * and `transferCarrierNotes` answers to `Stock` — one right over the whole warehouse, with no
 * reach that narrows it to a trolley of one's own, so the row adds nothing the category did not
 * already decide. It is passed anyway, because leaving it out is what makes a narrower rule
 * silently unanswerable the day one is added.
 */
export function TransferCarrierGeneral({ transferCarrierId }: TransferCarrierPanelBaseProps) {
	/** Hooks */
	const { t } = useTranslation();
	const { transferCarrier } = useTransferCarrier(transferCarrierId);

	/** Render */
	return (
		<Panels>
			<Panels.Panel flex={1}>
				<ContentCard
					title={t("general.general")}
					icon={icon("ShoppingCart")}
				>
					<DataGrid>
						<DataGrid.Row>
							<DataGrid.Label>{t("stock.transfer_carrier_no")}</DataGrid.Label>
							<DataGrid.Value>{guardString(transferCarrier?.number)}</DataGrid.Value>
						</DataGrid.Row>
						<DataGrid.Row>
							<DataGrid.Label>{t("general.name")}</DataGrid.Label>
							<DataGrid.Value>{guardString(transferCarrier?.name)}</DataGrid.Value>
						</DataGrid.Row>
						<DataGrid.Row>
							<DataGrid.Label>{t("general.description")}</DataGrid.Label>
							<DataGrid.Value>{guardString(transferCarrier?.description)}</DataGrid.Value>
						</DataGrid.Row>
						<DataGrid.Row>
							<DataGrid.Label>{t("stock.transfer_carrier_current_zone")}</DataGrid.Label>
							<DataGrid.Value>{guardString(transferCarrier?.currentZoneName)}</DataGrid.Value>
						</DataGrid.Row>
						<DataGrid.Row>
							<DataGrid.Label>{t("stock.transfer_carrier_open_legs")}</DataGrid.Label>
							<DataGrid.Value>
								<Tag
									color={(transferCarrier?.openLegCount ?? 0) > 0 ? "blue" : "gray"}
									variant="plain"
								>
									{transferCarrier?.openLegCount ?? 0}
								</Tag>
							</DataGrid.Value>
						</DataGrid.Row>
					</DataGrid>
				</ContentCard>
				<TransferCarrierLabel transferCarrierId={transferCarrierId} />
			</Panels.Panel>
			<Panels.Panel flex={2}>
				<Suspense fallback={<LazyFallback variant="inline" />}>
					<Notes
						notesTable="transferCarrierNotes"
						identifier={transferCarrierId}
						parent={transferCarrier}
					/>
				</Suspense>
			</Panels.Panel>
		</Panels>
	);
}
